import { toast } from "@/components/common/Toast/Toast"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import {
  useRemapArticle,
  useSearchArticles,
} from "../../../../../api/declarationsApi"
import type { Article, CandidateArticle } from "../../../../../api/types"

/** The supplier's own number to show beside a search result: the active
 *  link where one exists, else the first, mirroring the backend's own
 *  "primary supplier" pick (`_enrich_articles`, mc_master_data_service.py).
 *  Duplicated from `reviewmask/ArticleSlot.tsx` on purpose: this component
 *  is the Article Browser's own copy of the "zuordnen" affordance, kept
 *  independent so the browser's row shape stays free of the card's own
 *  popover wiring. */
const primarySupplierCode = (a: Article): string | undefined => {
  const primary =
    a.suppliers?.find((s) => s.active !== false) ?? a.suppliers?.[0]
  return primary?.supplierArticleNumber
}

/**
 * The "zuordnen" popover for one unmapped article row: picks a master-data
 * target (search or typed free text) and remaps the supplier's own number
 * to it via `useRemapArticle`. Mirrors `reviewmask/ArticleSlot.tsx`'s own
 * `RemapPopover` behaviour exactly (same translation keys, same commit
 * path), reimplemented here rather than imported so the Article Browser's
 * row markup stays independent of the card's.
 */
export function RemapPopover({
  declId,
  article,
  onLearned,
}: {
  declId: string
  article: Pick<CandidateArticle, "articleNumber" | "supplierCode">
  /** Fired once the remap mutation actually succeeds: the row's own
   *  "gemerkt" chip. */
  onLearned: () => void
}) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const { data: results } = useSearchArticles(declId, query)
  const remap = useRemapArticle()

  // ONE commit path for both a picked search result AND typed free text:
  // `useRemapArticle` PUTs `articleNumber` as a plain string, never a
  // resolved Article id, so a number the search never returned still
  // reaches the exact same mutation a picked result would.
  const commit = (articleNumber: string) => {
    remap.mutate(
      {
        declarationId: declId,
        supplierCode: article.supplierCode ?? article.articleNumber,
        articleNumber,
      },
      {
        onSuccess: () => {
          setOpen(false)
          onLearned()
          toast.success(translate("MC_DECL_V2_ARTS_MAP_SUCCESS"))
        },
      },
    )
  }

  const pick = (target: Article) => commit(target.articleNumber)

  // Enter always commits the typed text verbatim: it does not pick the top
  // search result, so a supplier number that finds no matches is still one
  // keystroke from being mapped.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return
    e.preventDefault()
    const value = query.trim()
    if (!value) return
    commit(value)
  }

  return (
    <span className="sv2-mapwrap">
      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button type="button" className="sv2-mapbtn">
                  {translate("MC_DECL_V2_ARTS_ZUORDNEN")}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">
              {translate("MC_DECL_V2_ARTS_ZUORDNEN_TITLE")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <PopoverContent
          // Without this the popover portals to document.body, outside
          // `.sv2-root`: its scoped CSS variables do not resolve there.
          disablePortal
          className="w-[296px] rounded-none border-0 bg-transparent p-0 shadow-none"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sv2-mappop" style={{ position: "static" }}>
            <div className="sv2-mh">
              {translate("MC_DECL_V2_ARTS_MAP_SEARCH_PLACEHOLDER")}
            </div>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={translate("MC_DECL_V2_ARTS_MAP_SEARCH_PLACEHOLDER")}
              className="sv2-acin"
            />
            <div className="sv2-maplist">
              {query.length >= 2 && (results?.length ?? 0) === 0 && (
                <p className="sv2-acempty">
                  {translate("MC_DECL_V2_ARTS_MAP_EMPTY")}
                </p>
              )}
              {results?.map((a) => {
                const supplierCode = primarySupplierCode(a)
                return (
                  <button
                    key={a.articleNumber}
                    type="button"
                    onClick={() => pick(a)}
                    className="sv2-acitem w-full text-left"
                  >
                    <span className="sv2-an">{a.name}</span>
                    <span className="sv2-apo">
                      {a.articleNumber}
                      {supplierCode && supplierCode !== a.articleNumber && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="sv2-supno">
                                {" "}
                                · {supplierCode}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {translate("MC_DECL_SB_SUPPLIER_CODE")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
            <div className="sv2-mapfree">
              {translate("MC_DECL_V2_ARTS_MAP_FREE_HINT")}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  )
}
